import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { apiFetch, ApiClientError } from "@/lib/api-client";
import { queryKeys } from "@/lib/query-keys";
import type { SubmissionDownstreamKind } from "@colab/shared";

export type AcceptSubmissionInput = { id: string };

export type AcceptSubmissionResponse = {
  kind: SubmissionDownstreamKind | null;
  downstreamId: string | null;
  partnerId: string | null;
};

// POST /api/admin/submissions/:id/accept
//
// Pessimistic — accept may create a downstream deal/lead and dispatch
// notifications; accuracy matters more than speed. The route is idempotent
// (re-accept on an already-accepted submission returns success with the
// existing downstream id), so this hook treats every 2xx as ordinary
// success.
//
// Cross-key invalidation:
//   - submissions.list() + submissions.detail(id) — base inbox refresh
//   - if downstream.kind === "deal": deals.list() + partners.detail(partnerId)
//     + partners.deals(partnerId) — the new deal needs to surface in partner
//     and deals caches
//
// Bucket-7 convention: each invalidation is an explicit invalidateQueries
// call, no parent-key shortcut. Per-key list is the documented contract.
export function useAcceptSubmission() {
  const queryClient = useQueryClient();
  return useMutation<AcceptSubmissionResponse, ApiClientError, AcceptSubmissionInput>({
    mutationFn: ({ id }) =>
      apiFetch<AcceptSubmissionResponse>(
        `/admin/submissions/${encodeURIComponent(id)}/accept`,
        { method: "POST" },
      ),
    onError: (err) => {
      toast.error(err.message ?? "Failed to accept submission");
    },
    onSettled: (data, _err, { id }) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.submissions.list() });
      queryClient.invalidateQueries({ queryKey: queryKeys.submissions.detail(id) });
      // Sub-bucket 17 — refresh the queue's 4 metric cards after status
      // transitions; the aggregate cache lives off the broader `list()`
      // prefix so the dedicated invalidate is necessary.
      queryClient.invalidateQueries({
        queryKey: queryKeys.submissions.aggregate(),
      });
      if (data?.kind === "deal") {
        queryClient.invalidateQueries({ queryKey: queryKeys.deals.list() });
        if (data.partnerId) {
          queryClient.invalidateQueries({
            queryKey: queryKeys.partners.detail(data.partnerId),
          });
          queryClient.invalidateQueries({
            queryKey: queryKeys.partners.deals(data.partnerId),
          });
        }
      }
    },
  });
}
